import { Settings } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  INSIDER_TRANSACTION_TYPE_DEFS,
  type InsiderTransactionCode,
} from '@/utils/insiderTradingTransactionTypes';

export function InsiderTradingLayerSettings({
  layerActive,
  enabledCodes,
  typeCounts,
  onEnabledCodesChange,
}: {
  layerActive: boolean;
  enabledCodes: ReadonlySet<InsiderTransactionCode>;
  typeCounts: ReadonlyMap<InsiderTransactionCode, number>;
  onEnabledCodesChange: (codes: Set<InsiderTransactionCode>) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const typeOptions = useMemo(() => {
    const known = new Set(INSIDER_TRANSACTION_TYPE_DEFS.map((d) => d.code));
    const extras = [...typeCounts.keys()].filter((c) => !known.has(c) && c !== '?');
    extras.sort();
    const extraDefs = extras.map((code) => ({
      code,
      label: `${code} — Other`,
      hint: 'Present in filings for this symbol',
      color: '#6b7280',
    }));
    return [...INSIDER_TRANSACTION_TYPE_DEFS, ...extraDefs];
  }, [typeCounts]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggleCode = (code: InsiderTransactionCode) => {
    const next = new Set(enabledCodes);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onEnabledCodesChange(next);
  };

  if (!layerActive) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Insider trading transaction type filter"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 ${
          open || enabledCodes.size < INSIDER_TRANSACTION_TYPE_DEFS.length
            ? 'bg-amber-50 text-amber-800'
            : 'text-amber-700 hover:bg-amber-50'
        }`}
      >
        <Settings className="h-3.5 w-3.5" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Insider trading transaction types"
          className="absolute left-0 top-full z-50 mt-1.5 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Transaction types
          </p>
          <div className="institutional-holders-ribbon-scroll max-h-64 overflow-y-auto">
            <div className="flex flex-col gap-0.5">
              {typeOptions.map((def) => {
                const checked = enabledCodes.has(def.code);
                const count = typeCounts.get(def.code) ?? 0;
                return (
                  <label
                    key={def.code}
                    className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors ${
                      checked ? 'bg-amber-50/80' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCode(def.code)}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-amber-600 focus:ring-amber-400"
                    />
                    <span
                      className="mt-1 h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: def.color }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-slate-800">{def.label}</span>
                        {count > 0 ? (
                          <span className="shrink-0 text-[10px] tabular-nums text-slate-400">
                            {count.toLocaleString()}
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-[10px] text-slate-500">{def.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="mt-1.5 flex gap-1 border-t border-slate-100 pt-1.5">
            <button
              type="button"
              className="flex-1 rounded-md px-2 py-1 text-[10px] font-medium text-amber-800 hover:bg-amber-50"
              onClick={() =>
                onEnabledCodesChange(new Set(typeOptions.map((d) => d.code)))
              }
            >
              Select all
            </button>
            <button
              type="button"
              className="flex-1 rounded-md px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
              onClick={() => onEnabledCodesChange(new Set())}
            >
              Clear all
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
